import React, { useMemo, useState, useEffect } from "react";
import { BiRefresh, BiCheckCircle, BiError } from "react-icons/bi";
import { ThemeContext } from "../../../themes/ThemeContext";
import TableSection from "../../Dashboards/components/TableSection";
import Loader from "../../../components/Loader/Loader";
import NoData from "../../../components/NoData/NoData";
import useRenderedTransactions from "../../../hooks/useRenderedTransactions";
import { getAllPartners } from "../../../data/api/api";
import axios from "axios";
import showToast from "../../../utils/toastUtils";

const Refunds = () => {
  const theme = React.useContext(ThemeContext);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [partnersError, setPartnersError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({});
  
  const { transactions: renderedTransactions, loading: renderedTransactionsLoading, error: renderedTransactionsError, refetch: refetchRenderedTransactions } = useRenderedTransactions(selectedPartnerId || null);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    setPartnersLoading(true);
    setPartnersError(null);
    try {
      const response = await getAllPartners(1, 100);
      if (response.status === 'success' && response.data?.partners) {
        const partnersList = response.data.partners;
        setPartners(partnersList);
        if (partnersList.length > 0 && !selectedPartnerId) {
          setSelectedPartnerId(partnersList[0].partnerId);
        }
      } else {
        setPartnersError(response.message || 'Failed to load partners');
      }
    } catch (error) {
      setPartnersError(error.message || 'Failed to load partners');
    } finally {
      setPartnersLoading(false);
    }
  };

  const safeTransactions = useMemo(() => {
    if (!renderedTransactions || !Array.isArray(renderedTransactions)) {
      return [];
    }
    
    return renderedTransactions.map((transaction) => {
      if (!transaction) {
        return null;
      }
      
      const transactionId = transaction.id || transaction.order_id || 'N/A';
      const initiationTranRef = transaction.initiation_tran_ref || transaction.initiationTranRef || '';

      return {
        ...transaction,
        id: transactionId,
        expected_amount: transaction.expected_amount || transaction.expectedAmount || '0',
        amount_paid: transaction.amount_paid || transaction.amountPaid || '0',
        status: transaction.status || 'Unknown',
        payer_account_name: transaction.payer_account_name || transaction.payerAccountName || 'N/A',
        payer_bank: transaction.payer_bank || transaction.payerBank || 'N/A',
        payer_account_number: transaction.payer_account_number || transaction.payerAccountNumber || 'N/A',
        recipient_account_number: transaction.recipient_account_number || transaction.recipientAccountNumber || 'N/A',
        initiation_tran_ref: initiationTranRef,
        trans_id: transaction.trans_id || transaction.transId || transaction.transaction_id || 'N/A',
        formatted_paid_at: transaction.formatted_paid_at || transaction.formattedPaidAt || transaction.paid_at || 'N/A',
        update_status: {
          transactionId,
          initiationTranRef
        }
      };
    }).filter(transaction => transaction !== null);
  }, [renderedTransactions]);

  const handleUpdateStatus = async (initiationTranRef, transactionId) => {
    if (!initiationTranRef || initiationTranRef === 'N/A' || initiationTranRef.trim() === '') {
      showToast("error", "Transaction reference is missing or invalid", 3000);
      return;
    }

    setUpdatingStatus(prev => ({ ...prev, [transactionId]: true }));

    try {
      const transactionBaseUrl = process.env.REACT_APP_API_TRANSACTION_BASE_URL || 'http://localhost:8000/api/transactions';
      const response = await axios.post(
        `${transactionBaseUrl}/rendered/update-status`,
        { initiationTranRef },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: true
        }
      );

      if (response.data.success) {
        showToast("success", response.data.message || "Transaction status updated successfully", 3000);
        refetchRenderedTransactions();
      } else {
        showToast("error", response.data.message || "Failed to update transaction status", 3000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || "An unexpected error occurred while updating transaction status";
      showToast("error", errorMessage, 3000);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [transactionId]: false }));
    }
  };

  const renderedTransactionsColumns = [
    { key: "id", label: "Order ID" },
    { 
      key: "expected_amount", 
      label: "Expected Amount",
      format: (value) => `₦${value}`
    },
    { 
      key: "amount_paid", 
      label: "Amount Paid",
      format: (value) => `₦${value}`
    },
    { 
      key: "status", 
      label: "Status",
      format: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value.toLowerCase() === 'exact' ? 'text-green-600 bg-green-100' :
          value.toLowerCase() === 'overpaid' ? 'text-blue-600 bg-blue-100' :
          value.toLowerCase() === 'underpaid' ? 'text-red-600 bg-red-100' :
          'text-gray-600 bg-gray-100'
        }`}>
          {value}
        </span>
      )
    },
    { key: "payer_account_name", label: "Payer Name" },
    { key: "payer_bank", label: "Payer Bank" },
    { key: "payer_account_number", label: "Payer Account" },
    { key: "recipient_account_number", label: "Recipient Account" },
    { key: "initiation_tran_ref", label: "Initiation Ref" },
    { key: "trans_id", label: "Transaction ID" },
    { key: "formatted_paid_at", label: "Paid At" },
    {
      key: "update_status",
      label: "Update Status",
      format: (value) => {
        if (!value || typeof value !== 'object') {
          return <span className="text-gray-400 text-xs">N/A</span>;
        }

        const { transactionId, initiationTranRef } = value;
        const isUpdating = updatingStatus[transactionId];
        const hasValidRef = initiationTranRef && initiationTranRef !== 'N/A' && initiationTranRef.trim() !== '';

        return (
          <button
            onClick={() => handleUpdateStatus(initiationTranRef, transactionId)}
            disabled={isUpdating || !hasValidRef}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
              hasValidRef
                ? isUpdating
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                : 'bg-gray-300 text-white cursor-not-allowed'
            }`}
            style={{
              background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)"
            }}
            title={!hasValidRef ? 'Transaction reference is missing' : 'Update transaction status'}
          >
            {isUpdating ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Updating...</span>
              </>
            ) : hasValidRef ? (
              <>
                <BiCheckCircle className="w-3 h-3" />
                <span>Update</span>
              </>
            ) : (
              <>
                <BiError className="w-3 h-3" />
                <span>N/A</span>
              </>
            )}
          </button>
        );
      }
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.colors.text.primary }}>
            Refunds
          </h2>
          <p className="text-gray-400">
            View all rendered transactions with payment status, amounts, and transaction details.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchPartners}
            disabled={partnersLoading}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
            style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}
            title="Refresh partners"
          >
            <BiRefresh className="w-4 h-4" />
            Refresh Partners
          </button>
        </div>
      </div>

      <div className="rounded-lg p-5" style={{ backgroundColor: theme.colors.background.secondary }}>
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: theme.colors.text.primary }}>
            Select Partner
          </label>
          {partnersLoading ? (
            <div className="text-sm text-gray-400">Loading partners...</div>
          ) : partnersError ? (
            <div className="text-sm text-red-400">{partnersError}</div>
          ) : (
            <select
              value={selectedPartnerId}
              onChange={(e) => setSelectedPartnerId(e.target.value)}
              className="w-full max-w-md px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              style={{
                backgroundColor: theme.colors.background.primary,
                borderColor: theme.colors.background.accent,
                color: theme.colors.text.primary
              }}
            >
              <option value="">Select a partner</option>
              {partners.map((partner) => (
                <option key={partner.partnerId} value={partner.partnerId}>
                  {partner.partnerId} - {partner.business_name || partner.first_name + ' ' + partner.last_name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="rounded-lg p-5" style={{ color: "#d2d2d2", backgroundColor: theme.colors.background.secondary }}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold" style={{ color: theme.colors.text.primary }}>Rendered Transactions</h3>
          {selectedPartnerId && (
            <button
              onClick={refetchRenderedTransactions}
              className="flex items-center gap-2 px-3 py-1.5 text-white rounded-lg hover:opacity-90 transition-colors text-sm"
              style={{ background: "linear-gradient(270deg, rgb(75, 73, 172) 0.05%, rgb(153, 153, 255) 99.97%)" }}
              title="Refresh transactions"
            >
              <BiRefresh className="w-4 h-4" />
              Refresh
            </button>
          )}
        </div>
        
        {!selectedPartnerId ? (
          <div className="text-center py-8">
            <p className="text-gray-400">Please select a partner to view rendered transactions</p>
          </div>
        ) : renderedTransactionsLoading ? (
          <Loader message="Loading rendered transactions..." />
        ) : renderedTransactionsError ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{renderedTransactionsError}</p>
                </div>
                <div className="mt-4">
                  <button
                    onClick={refetchRenderedTransactions}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : safeTransactions.length === 0 ? (
          <NoData message="No rendered transactions available" />
        ) : (
          <TableSection 
            data={safeTransactions} 
            columns={renderedTransactionsColumns} 
            theme={theme} 
            pagination={{
              currentPage: 1,
              perPage: safeTransactions.length,
              total: safeTransactions.length
            }}
            onPageChange={() => {}}
            onRowsPerPageChange={() => {}}
            showFilters={false}
            showExport={false}
          />
        )}
      </div>
    </div>
  );
};

export default Refunds;
